import React from 'react';

export interface SkeletonProps {
  className?: string;
  /** Any CSS width value (e.g. `120`, `'40%'`). Prefer Tailwind via className. */
  width?: number | string;
  /** Any CSS height value. Prefer Tailwind via className. */
  height?: number | string;
  rounded?: 'sm' | 'md' | 'full';
}

const roundedClasses: Record<NonNullable<SkeletonProps['rounded']>, string> = {
  sm: 'rounded',
  md: 'rounded-lg',
  full: 'rounded-full',
};

/**
 * Loading placeholder block. Always `aria-hidden`; wrap the loading region
 * in a container with `aria-busy="true"` (see `SkeletonList`).
 */
export const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  width,
  height,
  rounded = 'sm',
}) => {
  const style: React.CSSProperties = {};
  if (width !== undefined) style.width = width;
  if (height !== undefined) style.height = height;

  return (
    <div
      aria-hidden="true"
      data-testid="skeleton"
      className={`animate-pulse bg-gray-200 ${roundedClasses[rounded]} ${className}`.trim()}
      style={Object.keys(style).length ? style : undefined}
    />
  );
};

export interface SkeletonRowProps {
  className?: string;
}

/**
 * Placeholder for one flat list row (Template 1): 64px tall, two text bars
 * on the left and a status-pill placeholder on the right.
 */
export const SkeletonRow: React.FC<SkeletonRowProps> = ({ className = '' }) => (
  <div
    aria-hidden="true"
    data-testid="skeleton-row"
    className={`flex min-h-[64px] items-center justify-between gap-4 px-4 py-3 ${className}`.trim()}
  >
    <div className="flex min-w-0 flex-1 flex-col gap-2">
      <Skeleton className="h-4 w-2/5" />
      <Skeleton className="h-3 w-3/5" />
    </div>
    <Skeleton className="h-6 w-16" rounded="full" />
  </div>
);

export interface SkeletonListProps {
  rows?: number;
  className?: string;
}

/** `rows` skeleton rows separated by 1px dividers; marks itself `aria-busy`. */
export const SkeletonList: React.FC<SkeletonListProps> = ({
  rows = 6,
  className = '',
}) => (
  <div
    aria-busy="true"
    data-testid="skeleton-list"
    className={`divide-y divide-gray-200 ${className}`.trim()}
  >
    {Array.from({ length: rows }, (_, i) => (
      <SkeletonRow key={i} />
    ))}
  </div>
);

export default Skeleton;
